import {Pipe, PipeTransform} from '@angular/core';

/**
 * Formats a number for display in a tile:
 * - a non-breaking space (U+00A0) as the thousands separator once the integer part passes 3 digits,
 *   so a long number never wraps mid-value in a narrow tile
 * - a period as the decimal separator, whatever the locale
 * - configurable min/max fraction digits; by default the input's own precision is kept
 * - an empty string for `null`/`undefined`/`NaN`, so a template can render it unguarded
 *
 * `{{ 19000.119191919 | formatNumber }}` → `19 000.119191919`
 * `{{ 19000.119191919 | formatNumber: 0 : 2 }}` → `19 000.12`
 * `{{ 2196800 | formatNumber }}` → `2 196 800`
 */
@Pipe({
  name: 'formatNumber'
})
export class FormatNumberPipe implements PipeTransform {
  private readonly thousandsSeparator = '\u00a0';

  transform(value: number | null | undefined, minimumFractionDigits = 0, maximumFractionDigits = 15): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '';
    }

    const [integer, decimal] = new Intl.NumberFormat('en-US', {
      minimumFractionDigits,
      maximumFractionDigits,
      useGrouping: false
    }).format(value).split('.');

    const formattedInteger = integer.length > 3
      ? integer.replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandsSeparator)
      : integer;

    return decimal ? `${formattedInteger}.${decimal}` : formattedInteger;
  }
}
